import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth';
import {
  canArchivarEscrituras,
  checkAllPermissions,
  checkAnyPermissions,
  PERMISSIONS,
} from '@/auth/permissions';
import { usePreferences } from '@/auth/hooks/usePreferences';
import {
  TramiteStatusDialog,
  useTramiteStatusDialog,
} from '@/components/TramiteStatus';
import {
  TramiteTareasDialog,
  useTramiteTareasDialog,
} from '@/tramite-tareas';
import QuickAssignUserDialog from '@/components/QuickAssignUserDialog';
import { BloqueoTramiteDialog } from '@/components/BloqueoTramiteDialog';
import { useToggleBloqueoTramite } from '@/hooks/useToggleBloqueoTramite';
import { useArchivarTramite } from '@/hooks/useArchivarTramite';
import { ArchivarTramiteDialog } from '@/components/ArchivarTramiteDialog';
import { useAlert } from '@/hooks/useAlert';
import { cn } from '@/lib/utils';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';
import {
  getEscrituraKanbanStatus,
  getVisibleKanbanColumns,
  parseStoredKanbanSort,
  resolveColumnSort,
} from './kanbanUtils';
import {
  patchKanbanTramite,
  refreshKanbanTramite,
  relocateKanbanTramite,
  removeKanbanTramite,
  toKanbanPatch,
  useKanbanTramite,
} from './kanbanCache';
import { KanbanColumn } from './KanbanColumn';
import { ParticipantsDialog } from './ParticipantsDialog';
import type {
  EscrituraKanbanStatus,
  KanbanFilters,
  KanbanSort,
  KanbanSortByColumn,
  ParticipantGroup,
  TramiteKanbanItem,
} from './types';

interface TramitesKanbanProps {
  filters: KanbanFilters;
  onAssignedUserSave: (
    tramite: TramiteKanbanItem,
    assignedUser: UserOption | null,
  ) => Promise<void>;
}

interface ParticipantDialogState {
  tramiteId: number;
  group: ParticipantGroup;
}

/**
 * Tailwind necesita las clases literales, así que el ancho del tablero se elige
 * por cantidad de columnas visibles en vez de interpolarse.
 *
 * Con 4 o 5 columnas cada una tiene ancho fijo y el tablero se desplaza en
 * horizontal: el espacio disponible depende del sidebar y del zoom, no sólo del
 * viewport, así que repartir el ancho por breakpoint dejaba columnas ilegibles.
 * En celulares cada columna ocupa casi todo el ancho y se navega deslizando.
 */
const GRID_BY_COLUMN_COUNT: Record<number, string> = {
  1: 'max-w-md grid-cols-1',
  4: 'grid-flow-col auto-cols-[85%] sm:auto-cols-[18rem]',
  5: 'grid-flow-col auto-cols-[85%] sm:auto-cols-[18rem]',
};

export const TramitesKanban = ({
  filters,
  onAssignedUserSave,
}: TramitesKanbanProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];
  const canViewStatus =
    checkAllPermissions(permissions, [PERMISSIONS.MANAGE_TRAMITES.name]) ||
    checkAllPermissions(permissions, [PERMISSIONS.VIEW_TRAMITE_STATUS.name]);
  const canViewTareas =
    checkAllPermissions(permissions, [PERMISSIONS.MANAGE_TRAMITES.name]) ||
    checkAllPermissions(permissions, [PERMISSIONS.VIEW_TRAMITE_TAREAS.name]) ||
    checkAllPermissions(permissions, [PERMISSIONS.MANAGE_TRAMITE_TAREAS.name]);
  // Los mismos permisos que piden los íconos de TramiteBudgetIndicators: sin
  // ninguno, la card no deja una sección de indicadores vacía.
  const canViewIndicators = checkAnyPermissions(permissions, [
    PERMISSIONS.VIEW_COSTOS.name,
    PERMISSIONS.VIEW_PRESUPUESTOS_FILES.name,
    PERMISSIONS.VIEW_PRESUPUESTOS.name,
  ]);
  const canManageTramites = checkAllPermissions(permissions, [
    PERMISSIONS.MANAGE_TRAMITES.name,
  ]);
  const canBloquear = checkAllPermissions(permissions, [
    PERMISSIONS.MANAGE_BLOQUEO_TRAMITE.name,
  ]);
  const canArchivar = canArchivarEscrituras(permissions);
  const { questionAlert } = useAlert();

  // Reemplazo puntual en la caché en vez de invalidar el tablero: invalidar
  // `['tramites','kanban']` obliga a React Query a revalidar todas las páginas
  // cargadas de todas las columnas, una por una.
  //
  // El bloqueo además cambia de columna (las columnas son excluyentes), así que
  // después del patch la tarjeta se reubica.
  const applyBloqueoUpdate = (updated: TramiteKanbanItem) => {
    patchKanbanTramite(queryClient, updated.id, toKanbanPatch(updated));
    void relocateKanbanTramite(
      queryClient,
      updated.id,
      getEscrituraKanbanStatus(updated),
    );
  };

  const { toggle: toggleBloqueo } = useToggleBloqueoTramite(applyBloqueoUpdate);

  /**
   * ¿El filtro de archivado activo deja fuera a una escritura que quedó con
   * este valor? Si sí, la tarjeta se saca del tablero (y se corrige el total de
   * la columna); si no, alcanza con parchearla.
   */
  const archivadoFiltraFuera = (archivado: boolean) => {
    const filtro = filters.archivadoFilter || 'no';
    if (filtro === 'todas') return false;
    return filtro === 'si' ? !archivado : archivado;
  };

  const { toggle: toggleArchivado } = useArchivarTramite((updated) => {
    if (archivadoFiltraFuera(Boolean(updated.archivado))) {
      removeKanbanTramite(queryClient, updated.id);
      return;
    }
    patchKanbanTramite(queryClient, updated.id, toKanbanPatch(updated));
  });

  const [archivarDialogTramite, setArchivarDialogTramite] =
    useState<TramiteKanbanItem | null>(null);

  const [bloqueoDialogTramite, setBloqueoDialogTramite] =
    useState<TramiteKanbanItem | null>(null);
  const [participantDialog, setParticipantDialog] =
    useState<ParticipantDialogState | null>(null);
  const [assignmentDialogTramiteId, setAssignmentDialogTramiteId] =
    useState<number | null>(null);
  const [isSavingAssignedUser, setIsSavingAssignedUser] = useState(false);
  const { preferences, updatePartialPreference } = usePreferences();
  // El board sólo monta con las preferencias ya cargadas (Home espera a que
  // `preferencesLoading` sea false para poner viewMode en 'kanban')
  const [sortByColumn, setSortByColumn] = useState<KanbanSortByColumn>(() =>
    parseStoredKanbanSort(preferences?.tramites?.kanbanSort),
  );

  // Los diálogos leen la fila viva de la caché, no la foto del momento del clic
  const participantTramite = useKanbanTramite(participantDialog?.tramiteId);
  const assignmentTramite = useKanbanTramite(assignmentDialogTramiteId);

  const {
    isOpen: isStatusOpen,
    selectedTramite,
    openDialog: openStatusDialog,
    closeDialog: closeStatusDialog,
  } = useTramiteStatusDialog();
  const {
    isOpen: isTareasOpen,
    selectedTramite: selectedTareasTramite,
    openDialog: openTareasDialog,
    closeDialog: closeTareasDialog,
  } = useTramiteTareasDialog();

  // El filtro global "Bloqueada" elige columnas: con `si` queda sólo la columna
  // de bloqueadas, con `no` las cuatro del flujo normal.
  const visibleColumns = getVisibleKanbanColumns(filters.bloqueadaFilter);

  const handleViewTramite = (tramite: TramiteKanbanItem) => {
    navigate('/dashboard', { state: { tramite } });
  };

  const handleClickBloqueo = (tramite: TramiteKanbanItem) => {
    if (tramite.bloqueado) {
      questionAlert({
        title: 'Desbloquear escritura',
        text: '¿Esta seguro que desea desbloquear esta escritura?',
        icon: 'question',
      }).then((r) => {
        if (r.isConfirmed) {
          toggleBloqueo({ id: tramite.id, bloqueado: false }).catch(() => {
            /* el error ya se notifica en el hook */
          });
        }
      });
      return;
    }

    setBloqueoDialogTramite(tramite);
  };

  const handleClickArchivado = (tramite: TramiteKanbanItem) => {
    if (tramite.archivado) {
      questionAlert({
        title: 'Desarchivar escritura',
        text: '¿Esta seguro que desea desarchivar esta escritura?',
        icon: 'question',
      }).then((r) => {
        if (r.isConfirmed) {
          toggleArchivado({ id: tramite.id, archivado: false }).catch(() => {
            /* el error ya se notifica en el hook */
          });
        }
      });
      return;
    }

    setArchivarDialogTramite(tramite);
  };

  const persistSortByColumn = (next: KanbanSortByColumn) => {
    setSortByColumn(next);
    updatePartialPreference('tramites.kanbanSort', next);
  };

  const handleSortChange = (status: EscrituraKanbanStatus, sort: KanbanSort) => {
    persistSortByColumn({ ...sortByColumn, [status]: sort });
  };

  const handleSortReset = (status: EscrituraKanbanStatus) => {
    const { [status]: _removed, ...rest } = sortByColumn;
    persistSortByColumn(rest);
  };

  const getAssignedUserOption = (
    tramite: TramiteKanbanItem | undefined,
  ): UserOption | null => {
    const assignedUser = tramite?.assigned_users?.[0];
    if (!assignedUser) {
      return null;
    }

    return {
      id: String(assignedUser.id),
      nombre: assignedUser.name,
      email: assignedUser.email,
      userId: String(assignedUser.id),
      source: 'user',
    };
  };

  const handleSaveAssignedUser = async (
    assignedUser: UserOption | null,
  ) => {
    if (!assignmentTramite) {
      return;
    }

    try {
      setIsSavingAssignedUser(true);
      await onAssignedUserSave(assignmentTramite, assignedUser);
      setAssignmentDialogTramiteId(null);
    } finally {
      setIsSavingAssignedUser(false);
    }
  };

  return (
    <>
      <div
        className="overflow-x-auto snap-x snap-mandatory md:snap-none bg-slate-100/80 p-2"
        aria-label="Kanban de escrituras"
      >
        <div
          className={cn(
            'grid gap-3',
            GRID_BY_COLUMN_COUNT[visibleColumns.length] ?? 'grid-cols-1',
          )}
        >
          {visibleColumns.map((column) => (
            <KanbanColumn
              key={column.id}
              column={column}
              filters={filters}
              sort={resolveColumnSort(column.id, sortByColumn)}
              isDefaultSort={!sortByColumn[column.id]}
              onSortChange={(sort) => handleSortChange(column.id, sort)}
              onSortReset={() => handleSortReset(column.id)}
              canViewStatus={canViewStatus}
              canViewTareas={canViewTareas}
              canViewIndicators={canViewIndicators}
              canEditAssignedUser={canManageTramites}
              canBloquear={canBloquear}
              canArchivar={canArchivar}
              onViewTramite={handleViewTramite}
              onViewParticipants={(selectedTramite, group) =>
                setParticipantDialog({
                  tramiteId: selectedTramite.id,
                  group,
                })
              }
              onViewStatus={openStatusDialog}
              onViewTareas={openTareasDialog}
              onEditAssignedUser={(tramite) => setAssignmentDialogTramiteId(tramite.id)}
              onClickBloqueo={handleClickBloqueo}
              onClickArchivado={handleClickArchivado}
            />
          ))}
        </div>
      </div>

      {participantDialog && participantTramite && (
        <ParticipantsDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setParticipantDialog(null);
            }
          }}
          group={participantDialog.group}
          participants={participantTramite[participantDialog.group] ?? []}
          tramiteId={participantDialog.tramiteId}
        />
      )}

      <TramiteStatusDialog
        tramite={selectedTramite}
        isOpen={isStatusOpen}
        onClose={closeStatusDialog}
      />

      <TramiteTareasDialog
        tramite={selectedTareasTramite}
        isOpen={isTareasOpen}
        onClose={closeTareasDialog}
        // El diálogo no devuelve el trámite actualizado, así que se trae esa
        // única escritura en vez de revalidar el tablero entero.
        onChanged={() => {
          void refreshKanbanTramite(queryClient, selectedTareasTramite?.id);
        }}
      />

      {archivarDialogTramite && (
        <ArchivarTramiteDialog
          isOpen
          onClose={() => setArchivarDialogTramite(null)}
          bloqueadasCount={archivarDialogTramite.bloqueado ? 1 : 0}
          onConfirm={async (motivo) => {
            try {
              await toggleArchivado({ id: archivarDialogTramite.id, archivado: true, motivo });
              setArchivarDialogTramite(null);
            } catch {
              /* el error ya se notifica en el hook */
            }
          }}
        />
      )}

      <BloqueoTramiteDialog
        tramite={bloqueoDialogTramite}
        isOpen={!!bloqueoDialogTramite}
        onClose={() => setBloqueoDialogTramite(null)}
        onBlocked={applyBloqueoUpdate}
      />

      {assignmentDialogTramiteId !== null && assignmentTramite && (
        <QuickAssignUserDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setAssignmentDialogTramiteId(null);
            }
          }}
          entityLabel={`la escritura #${assignmentTramite.id}`}
          initialAssignedUser={getAssignedUserOption(assignmentTramite)}
          isSubmitting={isSavingAssignedUser}
          onSubmit={handleSaveAssignedUser}
        />
      )}
    </>
  );
};
